/**
 * GraphControls.tsx - Control panel for graph interactions
 * 
 * CHANGE HISTORY:
 * - 251105_1516 [pending] danielbeer: Replaced custom buttons/sliders with Skinnr components
 * - 251101_0758 [pending] danielbeer: Initial creation using Interfacr components
 * 
 * Provides zoom, pan, layout switching, and export controls for graphs
 * Uses Skinnr Button and Slider components for consistent theming
 */

import React from 'react';
import { Button, Slider } from '@opcentral/skinnr';
import type { GraphLayout } from '../types';
import './GraphControls.css';

export interface GraphControlsProps {
  // Layout
  currentLayout: GraphLayout;
  availableLayouts: GraphLayout[];
  onLayoutChange: (layout: GraphLayout) => void;
  
  // Zoom controls
  zoomLevel: number;
  onZoomIn: () => void;
  onZoomOut: () => void;
  onZoomReset: () => void;
  onZoomChange: (level: number) => void;
  minZoom?: number;
  maxZoom?: number;
  
  // Pan controls
  panMode: 'scroll' | 'drag';
  onPanModeChange: (mode: 'scroll' | 'drag') => void;
  onPanReset: () => void;
  
  // View controls
  onFitToView: () => void;
  onCenterView: () => void;
  
  // Export
  onExport?: (format: 'png' | 'svg' | 'json') => void;
  
  // Additional options
  showLabels: boolean;
  onToggleLabels: () => void;
  highlightConnected: boolean;
  onToggleHighlight: () => void;
  
  // Sensitivity controls
  panSensitivity?: number;
  onPanSensitivityChange?: (value: number) => void;
  nodeSpacing?: number;
  onNodeSpacingChange?: (value: number) => void;
}

export const GraphControls: React.FC<GraphControlsProps> = ({
  currentLayout,
  availableLayouts,
  onLayoutChange,
  zoomLevel,
  onZoomIn,
  onZoomOut,
  onZoomReset,
  onZoomChange,
  minZoom = 0.1,
  maxZoom = 4,
  panMode,
  onPanModeChange,
  onPanReset,
  onFitToView,
  onCenterView,
  onExport,
  showLabels,
  onToggleLabels,
  highlightConnected,
  onToggleHighlight,
  panSensitivity = 1.5,
  onPanSensitivityChange,
  nodeSpacing = 100,
  onNodeSpacingChange,
}) => {
  const layoutIcons: Record<GraphLayout, string> = {
    force: '🌐',
    tree: '🌳',
    radial: '⭕',
    sankey: '➡️',
    matrix: '⬜',
    dag: '📊',
  };

  const layoutNames: Record<GraphLayout, string> = {
    force: 'Force',
    tree: 'Tree',
    radial: 'Radial',
    sankey: 'Sankey',
    matrix: 'Matrix',
    dag: 'DAG',
  };

  return (
    <div className="graph-controls">
      {/* Layout Switcher */}
      <div className="graph-controls-section">
        <label className="graph-controls-label">Layout</label>
        <div className="graph-controls-buttons">
          {availableLayouts.map((layout) => (
            <Button
              key={layout}
              variant="secondary"
              size="sm"
              icon={layoutIcons[layout]}
              active={currentLayout === layout}
              onClick={() => onLayoutChange(layout)}
              title={layoutNames[layout]}
            >
              {layoutNames[layout]}
            </Button>
          ))}
        </div>
      </div>

      {/* Zoom Controls */}
      <div className="graph-controls-section">
        <Slider
          label="Zoom"
          showValue
          formatValue={(v) => `${Math.round(v * 100)}%`}
          min={minZoom}
          max={maxZoom}
          step={0.1}
          value={zoomLevel}
          onChange={(e) => onZoomChange(parseFloat(e.target.value))}
        />
        <div className="graph-controls-group">
          <Button
            variant="ghost"
            size="sm"
            icon="−"
            iconOnly
            onClick={onZoomOut}
            disabled={zoomLevel <= minZoom}
            title="Zoom Out (Ctrl + -)"
          />
          <Button
            variant="ghost"
            size="sm"
            icon="+"
            iconOnly
            onClick={onZoomIn}
            disabled={zoomLevel >= maxZoom}
            title="Zoom In (Ctrl + +)"
          />
          <Button
            variant="ghost"
            size="sm"
            icon="⊙"
            iconOnly
            onClick={onZoomReset}
            title="Reset Zoom (Ctrl + 0)"
          />
        </div>
      </div>

      {/* Pan Mode */}
      <div className="graph-controls-section">
        <label className="graph-controls-label">Pan Mode</label>
        <div className="graph-controls-buttons">
          <Button
            variant="secondary"
            size="sm"
            icon="🖱️"
            active={panMode === 'scroll'}
            onClick={() => onPanModeChange('scroll')}
            title="Scroll to Pan"
          >
            Scroll
          </Button>
          <Button
            variant="secondary"
            size="sm"
            icon="✋"
            active={panMode === 'drag'}
            onClick={() => onPanModeChange('drag')}
            title="Drag to Pan"
          >
            Drag
          </Button>
        </div>
      </div>

      {/* View Controls */}
      <div className="graph-controls-section">
        <label className="graph-controls-label">View</label>
        <div className="graph-controls-buttons">
          <Button
            variant="secondary"
            size="sm"
            icon="⛶"
            onClick={onFitToView}
            title="Fit to View (F)"
          >
            Fit
          </Button>
          <Button
            variant="secondary"
            size="sm"
            icon="⊕"
            onClick={onCenterView}
            title="Center View (C)"
          >
            Center
          </Button>
          <Button
            variant="secondary"
            size="sm"
            icon="↺"
            onClick={onPanReset}
            title="Reset Pan (R)"
          >
            Reset
          </Button>
        </div>
      </div>

      {/* Sensitivity Controls */}
      {(onPanSensitivityChange || onNodeSpacingChange) && (
        <div className="graph-controls-section">
          <label className="graph-controls-label">Sensitivity</label>
          
          {onPanSensitivityChange && (
            <Slider
              label="Pan/Scroll"
              showValue
              formatValue={(v) => `${v.toFixed(1)}x`}
              min={0.5}
              max={3}
              step={0.1}
              value={panSensitivity}
              onChange={(e) => onPanSensitivityChange(parseFloat(e.target.value))}
            />
          )}
          
          {onNodeSpacingChange && (
            <Slider
              label="Node Spacing"
              showValue
              formatValue={(v) => `${v}px`}
              min={50}
              max={300}
              step={10}
              value={nodeSpacing}
              onChange={(e) => onNodeSpacingChange(parseInt(e.target.value))}
            />
          )}
        </div>
      )}

      {/* Display Options */}
      <div className="graph-controls-section">
        <label className="graph-controls-label">Display</label>
        <div className="graph-controls-buttons">
          <Button
            variant="secondary"
            size="sm"
            icon="🏷️"
            active={showLabels}
            onClick={onToggleLabels}
            title="Toggle Labels (L)"
          >
            Labels
          </Button>
          <Button
            variant="secondary"
            size="sm"
            icon="💡"
            active={highlightConnected}
            onClick={onToggleHighlight}
            title="Highlight Connected (H)"
          >
            Highlight
          </Button>
        </div>
      </div>

      {/* Export */}
      {onExport && (
        <div className="graph-controls-section">
          <label className="graph-controls-label">Export</label>
          <div className="graph-controls-buttons">
            <Button
              variant="secondary"
              size="sm"
              icon="🖼️"
              onClick={() => onExport('png')}
              title="Export as PNG"
            >
              PNG
            </Button>
            <Button
              variant="secondary"
              size="sm"
              icon="📐"
              onClick={() => onExport('svg')}
              title="Export as SVG"
            >
              SVG
            </Button>
            <Button
              variant="secondary"
              size="sm"
              icon="📄"
              onClick={() => onExport('json')}
              title="Export Data as JSON"
            >
              JSON
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default GraphControls;
